import type { SlotPlanInfo, WeekDayNumber } from '@@partner:types:app'

import React, { useState, useMemo } from 'react'
import classNames from 'classnames'
import { WEEK_DAYS, groupByWeekDay } from './SlotTableState'

import { Form, Button } from 'rsuite'
import SlotTable from './SlotTable'
import SlotTableForm from './SlotTableForm'
import SlotTableCopier from './SlotTableCopier'

import './SlotTable.scss'
import { useFetchAllDeliveryZones } from '@@partner:graphql/query/deliveryZone/allDeliveryZones.gql'
import { GQL_ID } from '@@partner:types:graphql'
import OtherZoneSlotsCopier from './OtherZoneSlotsCopier'

export interface SlotTableContainerProps {
  value: SlotPlanInfo[]
  onChange: (updatedSlots: SlotPlanInfo[]) => void
}

const SlotTableContainer: React.FC<SlotTableContainerProps> = ({
  value = [],
  onChange,
  children
}) => {
  const [ currentWeekDay, setCurrentWeekDay ] = useState<WeekDayNumber>(0)

  const { data, loading, error } = useFetchAllDeliveryZones()

  const deliveryZones = data?.allDeliveryZones.edges ?? []


  const groupedValue = useMemo(() => groupByWeekDay(value), [ value ])

  const makeWeekDaySelectHandler = (weekDay: number) => (e: any) => {
    e.preventDefault()
    setCurrentWeekDay(weekDay)
  }

  const handleCurrentEditableSlotChange = (slot: SlotPlanInfo) => {
    // update existing
    if (slot.id) {
      onChange(
        value.map((existingSlotInfo) => {
          if (existingSlotInfo.id === slot.id) {
            return slot
          }
          return existingSlotInfo
        })
      )
    }
    else {
      onChange(value.concat({ ...slot, weekDay: currentWeekDay }))
    }
  }

  const handleSlotChange = (slot: Partial<SlotPlanInfo>) => {
    onChange(
      value.map((existingSlotInfo) => {
        if (
          existingSlotInfo === slot ||
          (slot.id && slot.id === existingSlotInfo.id)
        ) {
          return { ...existingSlotInfo, ...slot }
        }
        return existingSlotInfo
      })
    )
  }

  const handleCopyFromZone = (id: GQL_ID) => {
    const deliveryZone = data?.allDeliveryZones.edges.find(zone => zone.id === id)
    const plans = deliveryZone?.slotPlans.map(plan => ({
      ...plan,
      id: undefined,
      slotTimeout: plan.slotTimeout / 60000
    }))
    if (plans) {
      onChange(plans)
    }
  }

  const handleSlotDelete = (slot: SlotPlanInfo) => {
    onChange(value.filter((existingSlotInfo) => existingSlotInfo !== slot))
  }

  const handleCopyToCurrent = (day: WeekDayNumber) => {
    const source = value.filter((plan) => plan.weekDay === day)

    onChange(
      value
        .filter(
          (existingSlotInfo) => existingSlotInfo.weekDay !== currentWeekDay
        )
        .concat(
          source.map((sourcePlan) => ({
            ...sourcePlan,
            id: undefined,
            weekDay: currentWeekDay
          }))
        )
    )
  }

  return (
    <div>
      <Form.Group className="ShopForm__Slots__Table">
        {WEEK_DAYS.map((weekDay) => (
          <Button
            key={weekDay.value}
            onClick={makeWeekDaySelectHandler(weekDay.value)}
            className={classNames('ShopForm__Slots__Table__Day', {
              'ShopForm__Slots__Table__Day--active':
                weekDay.value === currentWeekDay
            })}
          >
            {weekDay.label}
          </Button>
        ))}
      </Form.Group>

      <SlotTable
        onChange={handleSlotChange}
        slots={groupedValue[currentWeekDay] ?? []}
        onDelete={handleSlotDelete}
      />

      <SlotTableCopier onCopy={handleCopyToCurrent} />
      <OtherZoneSlotsCopier deliveryZones={deliveryZones} onSelect={handleCopyFromZone} /> 

      {children}

      <SlotTableForm onUpdate={handleCurrentEditableSlotChange} />
    </div>
  )
}

export default SlotTableContainer
